import {
  type NavigatorApartment,
  unitSummarySchema,
  type NavigatorBuilding,
  type NavigatorLevel,
  type NavigatorResponse,
  navigatorResponseSchema,
} from '@sivan/shared';
import {
  type KeyboardEvent,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ApiError, NetworkError, apiGet, apiPost } from '../api/client';
import { apartmentHref } from '../apartment/location';
import { StatusBadge } from '../apartment/StatusBadge';
import { EXCEPTION_META, STAGE_META, defectMeta } from '../apartment/status';
import { chooseProject, useCurrentProject } from '../current-project';
import { LoadingBlock, Problem } from '../handover/common';
import { Structures } from '../master-data/ProjectAdmin';
import { useLoad, useWorkspace } from '../master-data/use-master-data';
import { useNarrowScreen } from '../master-data/use-narrow-screen';
import { navigate, useHash } from '../routing';
import { Breadcrumbs } from '../ui/Breadcrumbs';
import { EmptyState } from '../ui/EmptyState';
import { Icon } from '../ui/Icon';
import {
  FILTERS,
  type FilterId,
  type FloorGroup,
  type GridKey,
  describeApartment,
  filterById,
  filterCounts,
  floorsOf,
  formatNavigatorLocation,
  matchesSearch,
  moveInGrid,
  parseNavigatorLocation,
  searchProject,
} from './navigator-model';

const GRID_KEYS = new Set<string>([
  'ArrowLeft',
  'ArrowRight',
  'ArrowUp',
  'ArrowDown',
  'Home',
  'End',
]);

const labelsOf = (a: NavigatorApartment) => ({
  stage: STAGE_META[a.stage].label,
  defects: (() => {
    const meta = defectMeta(a.defects);
    return meta === null || meta.tone === 'neutral' ? null : meta.label;
  })(),
  exceptions: a.exceptions.map((e) => EXCEPTION_META[e].label),
});

/**
 * מבנה ודירות (U3, docs/product-ux 07): project → building → floor → apartment, as an abstract
 * section of the building (floors in their stored order, apartments by number; no physical
 * position exists in the data). Every status comes from the server. Opening an apartment leads
 * to its workspace; back returns to the same building and apartment.
 */
export function NavigatorScreen() {
  const [workspace, reload] = useWorkspace();
  const location = parseNavigatorLocation(useHash());
  const projects = workspace.state === 'ready' ? workspace.data.projects : [];
  const current = useCurrentProject(projects);
  const named = projects.find((p) => p.id === location.projectId)?.id;
  useEffect(() => {
    if (named !== undefined) chooseProject(named);
  }, [named]);
  if (workspace.state === 'loading') return <LoadingBlock />;
  if (workspace.state === 'forbidden') return <Problem text="אין לך גישה לפרויקטים." />;
  if (workspace.state === 'error')
    return (
      <Problem text="טעינת הפרויקטים נכשלה." requestId={workspace.requestId} onRetry={reload} />
    );
  const project = projects.find((p) => p.id === location.projectId) ?? current;
  if (project === undefined) return <p className="empty">אין פרויקטים זמינים עבורך.</p>;
  return <ProjectNavigator key={project.id} projectId={project.id} />;
}

function ProjectNavigator({ projectId }: { readonly projectId: string }) {
  const [notice, setNotice] = useState<string | null>(null);
  const [loaded, reload] = useLoad(`navigator:${projectId}`, () =>
    apiGet(`/projects/${projectId}/navigator`, navigatorResponseSchema),
  );
  if (loaded.state === 'loading') return <LoadingBlock />;
  if (loaded.state === 'forbidden')
    return (
      <EmptyState icon="lock" title="מבנה הפרויקט אינו זמין לך">
        <p>הצגת הבניינים והדירות דורשת הרשאת צפייה בדירות ובמבנה הפרויקט.</p>
      </EmptyState>
    );
  if (loaded.state === 'error')
    return (
      <Problem text="טעינת מבנה הפרויקט נכשלה." requestId={loaded.requestId} onRetry={reload} />
    );
  return (
    <Navigator
      data={loaded.data}
      notice={notice}
      onChanged={(text?: string) => {
        // The confirmation lives here, so it survives the reload of the structure.
        setNotice(text ?? null);
        reload();
      }}
    />
  );
}

function Navigator({
  data,
  notice,
  onChanged,
}: {
  readonly data: NavigatorResponse;
  readonly notice: string | null;
  readonly onChanged: (notice?: string) => void;
}) {
  const location = parseNavigatorLocation(useHash());
  const [managing, setManaging] = useState(false);
  // Buildings and floors, for whoever may add them: the same editor as the project setup.
  const manage = data.permissions.structureEdit && (
    <>
      <button
        type="button"
        className="button nav-manage__toggle"
        aria-expanded={managing}
        onClick={() => {
          setManaging((v) => !v);
        }}
      >
        <Icon name={managing ? 'x' : 'plus'} /> {managing ? 'סגירה' : 'הוספת בניין או קומות'}
      </button>
      {managing && (
        <section className="nav-manage" aria-label="בניינים וקומות">
          <Structures
            projectId={data.project.id}
            onChanged={() => {
              onChanged();
            }}
          />
        </section>
      )}
    </>
  );
  const narrow = useNarrowScreen();
  const [filter, setFilter] = useState<FilterId>('all');
  const [query, setQuery] = useState('');
  const [floorKey, setFloorKey] = useState('all');
  const total = data.buildings.reduce((n, b) => n + b.apartments.length, 0);
  const building =
    data.buildings.find((b) => b.id === location.buildingId) ??
    data.buildings.find((b) => b.apartments.length > 0) ??
    data.buildings[0];

  if (building === undefined)
    return (
      <>
        <Header data={data} total={total} />
        {manage}
        {!managing && (
          <EmptyState icon="building" title="אין עדיין בניינים בפרויקט">
            <p>מוסיפים בניין וקומות, ואחר כך דירות בכל קומה.</p>
          </EmptyState>
        )}
      </>
    );

  const choose = (id: string) => {
    setFloorKey('all');
    navigate(
      formatNavigatorLocation({ projectId: data.project.id, buildingId: id, apartmentId: null }),
      'replace',
    );
  };
  const open = (apartment: NavigatorApartment, inBuilding: NavigatorBuilding) => {
    // Remember where the user was, so "back" returns to this building and apartment.
    navigate(
      formatNavigatorLocation({
        projectId: data.project.id,
        buildingId: inBuilding.id,
        apartmentId: apartment.id,
      }),
      'replace',
    );
    navigate(apartmentHref(data.project.id, apartment.id), 'push');
  };
  const floors = floorsOf(building).filter((f) => floorKey === 'all' || f.key === floorKey);
  const props: ViewProps = {
    data,
    building,
    floors,
    allFloors: floorsOf(building),
    filter,
    query,
    floorKey,
    lastOpened: location.buildingId === building.id ? location.apartmentId : null,
    onFilter: setFilter,
    onQuery: setQuery,
    onFloor: setFloorKey,
    onBuilding: choose,
    onOpen: open,
    onChanged,
  };
  return (
    <div className={`nav${narrow ? ' nav--phone' : ''}`}>
      <Breadcrumbs items={[{ label: data.project.name, to: '#home' }, { label: 'מבנה ודירות' }]} />
      <Header data={data} total={total} />
      {notice !== null && (
        <p className="notice notice--success" role="status">
          {notice}
        </p>
      )}
      {manage}
      {narrow ? <PhoneNavigator {...props} /> : <DesktopNavigator {...props} />}
    </div>
  );
}

interface ViewProps {
  readonly data: NavigatorResponse;
  readonly building: NavigatorBuilding;
  readonly floors: readonly FloorGroup[];
  readonly allFloors: readonly FloorGroup[];
  readonly filter: FilterId;
  readonly query: string;
  readonly floorKey: string;
  readonly lastOpened: string | null;
  readonly onFilter: (id: FilterId) => void;
  readonly onQuery: (query: string) => void;
  readonly onFloor: (key: string) => void;
  readonly onBuilding: (id: string) => void;
  readonly onOpen: (apartment: NavigatorApartment, building: NavigatorBuilding) => void;
  readonly onChanged: (notice?: string) => void;
}

function Header({ data, total }: { readonly data: NavigatorResponse; readonly total: number }) {
  return (
    <header className="nav-head">
      <h1>מבנה ודירות</h1>
      <p>
        {data.project.name} · {total} דירות ב-{data.buildings.length}{' '}
        {data.buildings.length === 1 ? 'בניין' : 'בניינים'}
      </p>
    </header>
  );
}

function Toolbar(props: ViewProps) {
  const { data, building, filter, query, floorKey } = props;
  const counts = filterCounts(building.apartments);
  const searchId = useId();
  const matches = searchProject(data, query);
  const elsewhere = matches.filter((m) => m.building.id !== building.id);
  return (
    <div className="nav-toolbar">
      <div className="nav-toolbar__row">
        <label className="nav-search" htmlFor={searchId}>
          <Icon name="search" />
          <span className="visually-hidden">חיפוש דירה</span>
          <input
            id={searchId}
            type="search"
            inputMode="numeric"
            autoComplete="off"
            placeholder="מספר דירה או מספר מקורי"
            value={query}
            onChange={(e) => {
              props.onQuery(e.target.value);
            }}
            onKeyDown={(e) => {
              const first = matches[0];
              if (e.key === 'Enter' && first !== undefined) {
                e.preventDefault();
                props.onOpen(first.apartment, first.building);
              }
            }}
          />
        </label>
        <label className="nav-floor">
          <span>קומה</span>
          <select
            value={floorKey}
            onChange={(e) => {
              props.onFloor(e.target.value);
            }}
          >
            <option value="all">כל הקומות</option>
            {props.allFloors.map((f) => (
              <option key={f.key} value={f.key}>
                {f.label}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="nav-chips" role="group" aria-label="סינון לפי מצב">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            type="button"
            className="nav-chip"
            aria-pressed={f.id === filter}
            onClick={() => {
              props.onFilter(f.id);
            }}
          >
            {f.label} <span className="nav-chip__count">{counts[f.id]}</span>
          </button>
        ))}
      </div>
      <p className="nav-search-result" role="status">
        {query.trim() === ''
          ? ''
          : matches.length === 0
            ? 'לא נמצאה דירה במספר הזה (נוכחי או מקורי).'
            : `${matches.length === 1 ? 'התאמה אחת' : `${String(matches.length)} התאמות`} · Enter פותח את ${matches.length === 1 ? 'הדירה' : 'הראשונה'}`}
      </p>
      {elsewhere.length > 0 && (
        <ul className="nav-elsewhere" aria-label="התאמות בבניינים אחרים">
          {elsewhere.slice(0, 6).map((m) => (
            <li key={m.apartment.id}>
              <button
                type="button"
                className="button button--secondary"
                onClick={() => {
                  props.onOpen(m.apartment, m.building);
                }}
              >
                {m.building.name} · דירה {m.apartment.number}
                {m.apartment.sourceNumber !== null && ` (מקורי ${m.apartment.sourceNumber})`}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Buildings(props: ViewProps & { readonly variant: 'list' | 'chips' }) {
  const { data, building } = props;
  return (
    <nav
      className={props.variant === 'list' ? 'nav-buildings' : 'nav-buildings nav-buildings--chips'}
      aria-label="בניינים"
    >
      <ul>
        {data.buildings.map((b) => {
          const withDefects = b.apartments.filter(
            (a) => a.defects !== null && a.defects.open > 0,
          ).length;
          const final = b.apartments.filter(
            (a) => a.stage === 'final_in_progress' || a.stage === 'final_summary',
          ).length;
          return (
            <li key={b.id}>
              <button
                type="button"
                aria-pressed={b.id === building.id}
                onClick={() => {
                  props.onBuilding(b.id);
                }}
              >
                {props.variant === 'chips' && <Icon name="building" />}
                <strong>{b.name}</strong>
                <span className="nav-buildings__count">{b.apartments.length} דירות</span>
                {data.permissions.handoverView && (withDefects > 0 || final > 0) && (
                  <span className="nav-buildings__mini">
                    {withDefects} עם ליקויים · {final} בסופית
                  </span>
                )}
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

function Tile({
  apartment,
  active,
  dim,
  match,
  onOpen,
  onFocus,
}: {
  readonly apartment: NavigatorApartment;
  readonly active: boolean;
  readonly dim: boolean;
  readonly match: boolean;
  readonly onOpen: () => void;
  readonly onFocus: () => void;
}) {
  const stage = STAGE_META[apartment.stage];
  const open = apartment.defects?.open ?? 0;
  const awaiting = apartment.defects?.awaiting ?? 0;
  return (
    <button
      type="button"
      data-apartment={apartment.id}
      className={`tile tile--${apartment.stage}${dim ? ' is-dim' : ''}${match ? ' is-match' : ''}`}
      tabIndex={active ? 0 : -1}
      aria-label={describeApartment(apartment, labelsOf(apartment))}
      onClick={onOpen}
      onFocus={onFocus}
      onMouseEnter={onFocus}
    >
      <span className="tile__num">{apartment.number}</span>
      {apartment.sourceFloorLabel !== null && (
        <span className="tile__source">{apartment.sourceFloorLabel}</span>
      )}
      <span className="tile__status">
        <Icon name={stage.icon} />
        {stage.short}
      </span>
      <span className="tile__marks" aria-hidden="true">
        {open > 0 && <span className="mark-badge mark-badge--defects">{open}</span>}
        {awaiting > 0 && <span className="mark-badge mark-badge--awaiting">{awaiting}</span>}
        {apartment.exceptions.length > 0 && (
          <span className="mark-badge mark-badge--exception">!</span>
        )}
      </span>
    </button>
  );
}

function DesktopNavigator(props: ViewProps) {
  const { building, floors, filter, query } = props;
  const test = filterById(filter).test;
  const order = useMemo(() => floors.map((f) => f.apartments.map((a) => a.id)), [floors]);
  const first = order.flat()[0] ?? null;
  const remembered =
    props.lastOpened !== null && order.some((ids) => ids.includes(props.lastOpened ?? ''))
      ? props.lastOpened
      : null;
  const [activeId, setActiveId] = useState<string | null>(remembered ?? first);
  const active = order.some((ids) => ids.includes(activeId ?? '')) ? activeId : first;
  const gridRef = useRef<HTMLDivElement>(null);
  const focusTile = (id: string) => {
    gridRef.current?.querySelector<HTMLButtonElement>(`[data-apartment="${id}"]`)?.focus();
  };
  // Back from an apartment: the same apartment is focused and in view.
  useLayoutEffect(() => {
    if (remembered === null) return;
    const tile = gridRef.current?.querySelector<HTMLButtonElement>(
      `[data-apartment="${remembered}"]`,
    );
    tile?.focus({ preventScroll: true });
    tile?.scrollIntoView({ block: 'center' });
    // Only when arriving with a remembered apartment.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const byId = new Map(building.apartments.map((a) => [a.id, a]));
  const shown = active === null ? undefined : byId.get(active);
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!GRID_KEYS.has(event.key) || active === null) return;
    event.preventDefault();
    const next = moveInGrid(order, active, event.key as GridKey);
    setActiveId(next);
    focusTile(next);
  };
  return (
    <div className="nav-layout">
      <Buildings {...props} variant="list" />
      <section className="nav-main" aria-labelledby="nav-building-title">
        <Toolbar {...props} />
        <div className="nav-stage">
          <div className="elevation">
            <div className="elevation__title">
              <h2 id="nav-building-title">{building.name}</h2>
              <span>חתך לפי קומות · הסדר והמספור מנתוני האב, ללא מיקום פיזי</span>
            </div>
            {building.apartments.length === 0 ? (
              <p className="elevation__empty">אין עדיין דירות בבניין זה.</p>
            ) : (
              <div
                role="grid"
                aria-label={`${building.name}: דירות לפי קומות`}
                className="floors"
                ref={gridRef}
                onKeyDown={onKeyDown}
              >
                {floors.map((floor) => (
                  <div
                    key={floor.key}
                    role="row"
                    className={`floor${floor.apartments.length === 0 ? ' floor--empty' : ''}${floor.level !== null && floor.level.number === 0 ? ' floor--ground' : ''}`}
                  >
                    <div role="rowheader" className="floor__label">
                      {floor.label}
                      <small>
                        {floor.apartments.length === 1
                          ? 'דירה אחת'
                          : `${String(floor.apartments.length)} דירות`}
                      </small>
                    </div>
                    {floor.apartments.length === 0 ? (
                      <div role="gridcell" className="floor__rooms floor__rooms--empty">
                        אין דירות בקומה
                      </div>
                    ) : (
                      <div role="presentation" className="floor__rooms">
                        {floor.apartments.map((a) => (
                          <div role="gridcell" key={a.id} className="floor__cell">
                            <Tile
                              apartment={a}
                              active={a.id === active}
                              dim={!test(a)}
                              match={matchesSearch(a, query)}
                              onOpen={() => {
                                props.onOpen(a, building);
                              }}
                              onFocus={() => {
                                setActiveId(a.id);
                              }}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
            <div className="elevation__ground" aria-hidden="true" />
            <Legend />
          </div>
          <aside className="nav-details" aria-label="פרטי הדירה" aria-live="polite">
            {shown === undefined ? (
              <p className="nav-muted">בחרו דירה כדי לראות את פרטיה.</p>
            ) : (
              <Details apartment={shown} building={building} onOpen={props.onOpen} />
            )}
            <Unassigned data={props.data} />
          </aside>
        </div>
      </section>
    </div>
  );
}

function Details({
  apartment,
  building,
  onOpen,
}: {
  readonly apartment: NavigatorApartment;
  readonly building: NavigatorBuilding;
  readonly onOpen: ViewProps['onOpen'];
}) {
  const defects = defectMeta(apartment.defects);
  const floor = building.levels.find((l) => l.id === apartment.levelId)?.label ?? 'ללא קומה';
  return (
    <div className="nav-detail">
      <p className="nav-detail__title">
        {building.name} · דירה <bdi className="num">{apartment.number}</bdi>
      </p>
      <div className="nav-detail__badges">
        <StatusBadge meta={STAGE_META[apartment.stage]} />
        {defects !== null && <StatusBadge meta={defects} />}
        {apartment.exceptions.map((e) => (
          <StatusBadge key={e} meta={EXCEPTION_META[e]} />
        ))}
      </div>
      <dl className="nav-detail__facts">
        <div>
          <dt>קומה</dt>
          <dd>
            {floor}
            {apartment.sourceFloorLabel !== null && ` (במקור: ${apartment.sourceFloorLabel})`}
          </dd>
        </div>
        <div>
          <dt>מס׳ מקורי</dt>
          <dd>{apartment.sourceNumber ?? '—'}</dd>
        </div>
        <div>
          <dt>טיפוס</dt>
          <dd>{apartment.modelType ?? 'חסר'}</dd>
        </div>
        <div>
          <dt>חניה · מחסן</dt>
          <dd>
            {apartment.parking === 0 ? 'אין חניה' : `${String(apartment.parking)} חניה`} ·{' '}
            {apartment.storage === 0 ? 'אין מחסן' : `${String(apartment.storage)} מחסן`}
          </dd>
        </div>
      </dl>
      <button
        type="button"
        className="button button--primary"
        onClick={() => {
          onOpen(apartment, building);
        }}
      >
        לסביבת הדירה
      </button>
    </div>
  );
}

/** Spaces no apartment holds: free, or held while disputed (the dispute itself is not recorded). */
function Unassigned({ data }: { readonly data: NavigatorResponse }) {
  if (data.unassignedSpaces.length === 0) return null;
  const parking = data.unassignedSpaces.filter((s) => s.kind === 'parking');
  const storage = data.unassignedSpaces.filter((s) => s.kind === 'storage');
  return (
    <details className="nav-unassigned">
      <summary>
        <Icon name="alert" />
        {data.unassignedSpaces.length} חניות ומחסנים ללא שיוך
      </summary>
      <p className="nav-muted">
        פנויים, או מוחזקים בגלל מחלוקת. המחלוקת עצמה אינה נשמרת במערכת עדיין, ולכן אינה משויכת לדירה
        מסוימת.
      </p>
      <ul>
        {[...parking, ...storage].map((s) => (
          <li key={`${s.kind}:${s.number}:${s.location}`}>
            {s.kind === 'parking' ? 'חניה' : 'מחסן'} <bdi className="num">{s.number}</bdi>
            {s.location !== '' && <span className="nav-muted"> · {s.location}</span>}
          </li>
        ))}
      </ul>
    </details>
  );
}

function Legend() {
  return (
    <ul className="nav-legend" aria-label="מקרא">
      <li>
        <i className="swatch swatch--not_started" aria-hidden="true" />
        טרם החלה
      </li>
      <li>
        <i className="swatch swatch--initial" aria-hidden="true" />
        מסירה ראשונית
      </li>
      <li>
        <i className="swatch swatch--final" aria-hidden="true" />
        מסירה סופית
      </li>
      <li>
        <span className="mark-badge mark-badge--defects" aria-hidden="true">
          3
        </span>
        ליקויים פתוחים
      </li>
      <li>
        <span className="mark-badge mark-badge--awaiting" aria-hidden="true">
          2
        </span>
        לבדיקה חוזרת
      </li>
      <li>
        <span className="mark-badge mark-badge--exception" aria-hidden="true">
          !
        </span>
        חריג: נתון חסר או מצב לא עקבי
      </li>
      <li className="nav-legend__note">"סוכמה" = כל הפריטים נבדקו, לא חתום · אין מצב "נמסרה"</li>
    </ul>
  );
}

function PhoneNavigator(props: ViewProps) {
  const { building, floors, filter, query } = props;
  const test = filterById(filter).test;
  const searching = query.trim() !== '';
  const visible = (a: NavigatorApartment) => (searching ? matchesSearch(a, query) : test(a));
  const firstWithApartments = floors.find((f) => f.apartments.some(visible))?.key;
  const adding = props.data.permissions.unitCreate && !searching && filter === 'all';
  // Back from an apartment: its floor is open and the apartment in view.
  const listRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (props.lastOpened === null) return;
    listRef.current
      ?.querySelector<HTMLAnchorElement>(`[data-apartment="${props.lastOpened}"]`)
      ?.scrollIntoView({ block: 'center' });
    // Only when arriving with a remembered apartment.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div className="nav-phone">
      <Buildings {...props} variant="chips" />
      <Toolbar {...props} />
      <h2 className="nav-phone__title">{building.name}</h2>
      <div ref={listRef} className="m-floors">
        {floors
          // While searching or filtering, only floors with matching apartments.
          .filter((f) =>
            searching || filter !== 'all'
              ? f.apartments.some(visible)
              : f.apartments.length > 0 || (adding && f.level !== null),
          )
          .map((floor) => {
            const shown = floor.apartments.filter(visible);
            const openFloor =
              floor.key === firstWithApartments ||
              floor.apartments.some((a) => a.id === props.lastOpened) ||
              (searching && shown.length > 0) ||
              (filter !== 'all' && shown.length > 0) ||
              // An empty floor is open for whoever adds apartments: its action is in view.
              (adding && floor.apartments.length === 0);
            return (
              <details key={floor.key} className="m-floor" open={openFloor}>
                <summary>
                  <span>{floor.label}</span>
                  <span className="nav-muted">
                    {shown.length}/{floor.apartments.length}
                  </span>
                </summary>
                {shown.length === 0 ? (
                  <p className="nav-muted m-floor__empty">
                    {floor.apartments.length === 0
                      ? 'אין עדיין דירות בקומה זו.'
                      : 'אין דירות מתאימות בקומה זו.'}
                  </p>
                ) : (
                  <ul>
                    {shown.map((a) => {
                      const defects = defectMeta(a.defects);
                      return (
                        <li key={a.id}>
                          <a
                            className="m-apt"
                            data-apartment={a.id}
                            href={apartmentHref(props.data.project.id, a.id)}
                            aria-label={describeApartment(a, labelsOf(a))}
                            onClick={(e) => {
                              e.preventDefault();
                              props.onOpen(a, building);
                            }}
                          >
                            <span className="m-apt__num">{a.number}</span>
                            <span className="m-apt__badges">
                              <StatusBadge meta={STAGE_META[a.stage]} />
                              {defects !== null && defects.tone !== 'neutral' && (
                                <StatusBadge meta={defects} />
                              )}
                              {a.exceptions.map((e) => (
                                <StatusBadge key={e} meta={EXCEPTION_META[e]} />
                              ))}
                              {a.sourceFloorLabel !== null && (
                                <span className="m-apt__source">במקור: {a.sourceFloorLabel}</span>
                              )}
                            </span>
                            <Icon name="chevron-left" />
                          </a>
                        </li>
                      );
                    })}
                  </ul>
                )}
                {adding && floor.level !== null && (
                  <AddApartment
                    projectId={props.data.project.id}
                    building={building}
                    level={floor.level}
                    onAdded={props.onChanged}
                  />
                )}
              </details>
            );
          })}
      </div>
      {(searching || filter !== 'all') && !floors.some((f) => f.apartments.some(visible)) && (
        <p className="nav-muted">אין דירות מתאימות בבניין זה.</p>
      )}
      <Unassigned data={props.data} />
    </div>
  );
}

/**
 * "הוספת דירה" in a floor, on a phone: the apartment number only. The apartment's code is the
 * building code and the number (A-12); the server refuses a number already in the building.
 */
function AddApartment({
  projectId,
  building,
  level,
  onAdded,
}: {
  readonly projectId: string;
  readonly building: NavigatorBuilding;
  readonly level: NavigatorLevel;
  readonly onAdded: (notice: string) => void;
}) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const [number, setNumber] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!open)
    return (
      <div className="m-floor__add">
        <button
          type="button"
          className="button"
          onClick={() => {
            setOpen(true);
            setError(null);
          }}
        >
          <Icon name="plus" /> הוספת דירה ב{level.label}
        </button>
      </div>
    );
  const value = number.trim();
  return (
    <form
      className="m-floor__add"
      onSubmit={(e) => {
        e.preventDefault();
        if (value === '' || busy) return;
        setBusy(true);
        setError(null);
        apiPost(
          `/projects/${projectId}/units`,
          {
            code: `${building.code}-${value}`,
            displayNumber: value,
            kind: 'apartment',
            structureId: building.id,
            levelId: level.id,
          },
          unitSummarySchema,
        )
          .then(() => {
            setNumber('');
            setOpen(false);
            onAdded(`דירה ${value} נוספה ל${level.label}.`);
          })
          .catch((error: unknown) => {
            setError(
              error instanceof NetworkError
                ? 'אין חיבור לשרת. הדירה לא נוספה.'
                : error instanceof ApiError && (error.status === 409 || error.status === 422)
                  ? `לא ניתן להוסיף את דירה ${value}: המספר כבר קיים בבניין, או שאינו תקין.`
                  : error instanceof ApiError && error.status === 403
                    ? 'אין לך הרשאה להוסיף דירות.'
                    : 'הוספת הדירה נכשלה.',
            );
          })
          .finally(() => {
            setBusy(false);
          });
      }}
    >
      <label className="field" htmlFor={`${id}-number`}>
        <span>מספר הדירה</span>
      </label>
      <input
        id={`${id}-number`}
        value={number}
        maxLength={12}
        autoComplete="off"
        enterKeyHint="done"
        autoFocus
        onChange={(e) => {
          setNumber(e.target.value);
        }}
      />
      {error !== null && (
        <p className="notice notice--error" role="alert">
          {error}
        </p>
      )}
      <div className="m-floor__add-actions">
        <button type="submit" className="button button--primary" disabled={busy || value === ''}>
          {busy ? 'מוסיף…' : 'הוספה'}
        </button>
        <button
          type="button"
          className="button button--ghost"
          onClick={() => {
            setOpen(false);
            setNumber('');
            setError(null);
          }}
        >
          ביטול
        </button>
      </div>
    </form>
  );
}
